import { useEffect } from 'react';
import { parse } from 'qs';
import { useAtomValue } from 'jotai';
import {
  getRestoredSearchFilterStorage,
  updateFilterStorage,
} from '@/app/_components/feature/plp-srp-filter/filter-storage';
import { searchTypeAtom, searchTypeKeyAtom } from '@/app/_shared/store/search-filter';

/** 스크롤 복원을 반복적으로 체크하는 함수 */
export const checkAndRestoreScroll = (scrollY: number, onComplete?: () => void) => {
  const interval = 10; // 체크 주기 (ms)
  const maxWaitTime = 1000; // 최대 대기 시간 (ms)
  const startTime = Date.now(); // 시작 시간 기록

  const intervalId = setInterval(() => {
    const elapsed = Date.now() - startTime;

    // (1) 문서의 높이가 저장된 스크롤 위치보다 크거나 같을 때
    // (2) 최대 대기 시간을 초과했을 때
    if (document.body.scrollHeight >= scrollY || elapsed >= maxWaitTime) {
      window.scrollTo(0, scrollY); // 저장된 위치로 스크롤 이동
      clearInterval(intervalId); // 체크 작업 종료

      // 완료 콜백 함수 실행
      setTimeout(() => onComplete?.(), 500);
    }
  }, interval);
};

/** 스크롤 위치를 복원하는 함수 */
const restoreScrollPosition = (sessionKey: string, searchType: string | undefined, onComplete?: () => void) => {
  const currentQuery = parse(location.search.replace('?', ''));
  const restoredSearchFilter = getRestoredSearchFilterStorage({
    sessionKey,
  });

  const { scrollY = 0 } = restoredSearchFilter?.[currentQuery.pageKey as string]?.[searchType || 'all'] || {};

  // 스크롤 위치가 0인 경우 복원 작업 종료
  if (!scrollY) return;

  checkAndRestoreScroll(scrollY, onComplete);
};

/** 스크롤 위치의 저장과 복원을 관리하는 커스텀 훅 */
export const useScrollRestoration = (sessionKey: string, onComplete?: () => void) => {
  const searchType = useAtomValue(searchTypeAtom);
  const searchTypeKey = useAtomValue(searchTypeKeyAtom);

  useEffect(() => {
    // 현재 스크롤 위치를 저장하는 함수
    const saveScrollPosition = () => {
      const currentQuery = parse(location.search.replace('?', ''));
      updateFilterStorage({
        query: currentQuery,
        scrollY: window.scrollY,
        sessionKey,
        searchType: currentQuery[searchTypeKey] as string | undefined,
      });
    };

    // 초기 스크롤 위치 복원
    restoreScrollPosition(sessionKey, searchType, onComplete);

    // 페이지 이동 전 스크롤 위치 저장
    window.addEventListener('beforeunload', saveScrollPosition);
    return () => window.removeEventListener('beforeunload', saveScrollPosition);
  }, []);
};
